import {z} from 'zod'

import {tzDateFromIso} from 'src/api/zod-helpers'

const dispatchReportLocationRawSchema = z.object({
    name: z.string(),
    notes: z.string().nullable(),
})

const dispatchReportWorkerRequirementRawSchema = z.object({
    worker_type_name: z.string(),
    quantity: z.number(),
})

const dispatchReportWorkerAssignmentRawSchema = z.object({
    worker_name: z.string(),
})

const dispatchReportAssetRequirementRawSchema = z.object({
    rental_asset_type_name: z.string(),
    quantity: z.number(),
})

const dispatchReportAssetAssignmentRawSchema = z.object({
    rental_asset_name: z.string().nullable(),
    rental_asset_type_name: z.string().nullable(),
    quantity: z.number().nullable(),
})

const dispatchReportJobNoteRawSchema = z.object({
    content: z.string(),
})

const dispatchReportJobRawSchema = z.object({
    id: z.string(),
    display_id: z.string(),
    created_at: z.string(),
    status: z.enum(['planned', 'active']),
    start_date: z.string(),
    start_time: z.string().nullable(),
    end_date: z.string().nullable(),
    customer_id: z.string(),
    customer_name: z.string(),
    project_id: z.string(),
    project_notes: z.string().nullable(),
    po_number: z.string().nullable(),
    locations: z.array(dispatchReportLocationRawSchema),
    worker_requirements: z.array(dispatchReportWorkerRequirementRawSchema),
    worker_assignments: z.array(dispatchReportWorkerAssignmentRawSchema),
    asset_requirements: z.array(dispatchReportAssetRequirementRawSchema),
    asset_assignments: z.array(dispatchReportAssetAssignmentRawSchema),
    job_comments: z.array(dispatchReportJobNoteRawSchema),
})

export const dispatchReportRawSchema = z.object({
    report_date: z.string(),
    jobs: z.array(dispatchReportJobRawSchema),
})

export const dispatchReportSchema = dispatchReportRawSchema.transform((raw) => ({
    reportDate: raw.report_date,
    jobs: raw.jobs.map((job) => ({
        id: job.id,
        displayId: job.display_id,
        createdAt: tzDateFromIso.parse(job.created_at),
        status: job.status,
        startDate: job.start_date,
        startTime: job.start_time,
        endDate: job.end_date,
        customerId: job.customer_id,
        customerName: job.customer_name,
        projectId: job.project_id,
        projectNotes: job.project_notes,
        poNumber: job.po_number,
        locations: job.locations.map((location) => ({
            name: location.name,
            notes: location.notes,
        })),
        workerRequirements: job.worker_requirements.map((requirement) => ({
            workerTypeName: requirement.worker_type_name,
            quantity: requirement.quantity,
        })),
        workerAssignments: job.worker_assignments.map((assignment) => ({
            workerName: assignment.worker_name,
        })),
        assetRequirements: job.asset_requirements.map((requirement) => ({
            rentalAssetTypeName: requirement.rental_asset_type_name,
            quantity: requirement.quantity,
        })),
        assetAssignments: job.asset_assignments.map((assignment) => ({
            rentalAssetName: assignment.rental_asset_name,
            rentalAssetTypeName: assignment.rental_asset_type_name,
            quantity: assignment.quantity,
        })),
        jobComments: job.job_comments.map((comment) => ({
            content: comment.content,
        })),
    })),
}))

export type DispatchReport = z.infer<typeof dispatchReportSchema>
export type DispatchReportJob = DispatchReport['jobs'][number]
